import { Icon, cn } from '@elva/ui';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '../auth/authStore';
import { useLocationStore } from './locationStore';
import { useCoreLocations } from './useCoreLocations';
import { resolveCoreLocationNumericId } from './resolveCoreLocation';
import { useWorkspaceSublabel } from './useWorkspaceLabel';
import type { LocationSummary } from '../auth/types';

const LIST_GAP_PX = 6;
const LIST_MAX_HEIGHT_PX = 240;

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return `${first[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

const eyebrowClass =
  'font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--sidebar-muted)]';

interface FloatingListPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

function computeFloatingPosition(
  anchor: DOMRect,
  preferredWidth: number,
): FloatingListPosition {
  const maxListHeight = Math.min(LIST_MAX_HEIGHT_PX, window.innerHeight * 0.5);
  const spaceBelow = window.innerHeight - anchor.bottom - LIST_GAP_PX;
  const spaceAbove = anchor.top - LIST_GAP_PX;
  const openUpward = spaceBelow < maxListHeight && spaceAbove >= spaceBelow;

  const width = Math.max(anchor.width, preferredWidth);
  const left = Math.min(
    Math.max(anchor.left, LIST_GAP_PX),
    window.innerWidth - width - LIST_GAP_PX,
  );

  if (openUpward) {
    return {
      bottom: window.innerHeight - anchor.top + LIST_GAP_PX,
      left,
      width,
      maxHeight: Math.min(maxListHeight, spaceAbove),
    };
  }

  return {
    top: anchor.bottom + LIST_GAP_PX,
    left,
    width,
    maxHeight: Math.min(maxListHeight, spaceBelow),
  };
}

export interface LocationWorkspaceSwitcherProps {
  collapsed?: boolean;
  placement?: 'sidebar' | 'header';
}

export function LocationWorkspaceSwitcher({
  collapsed = false,
  placement = 'sidebar',
}: LocationWorkspaceSwitcherProps) {
  const accountLocations = useAuthStore((s) => s.locations);
  const accessLocationAscendId = useAuthStore((s) => s.access?.location_ascend_id);
  const activeAccessLocation = useAuthStore((s) => s.access?.location);
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const selectedLocationUuid = useLocationStore((s) => s.selectedLocationUuid);
  const setSelectedLocationUuid = useLocationStore((s) => s.setSelectedLocationUuid);
  const setSelectedCoreLocationId = useLocationStore((s) => s.setSelectedCoreLocationId);
  const switchActiveLocation = useAuthStore((s) => s.switchActiveLocation);
  const { data: coreLocationsData } = useCoreLocations();
  const coreLocations = useMemo(() => coreLocationsData ?? [], [coreLocationsData]);
  const workspaceSublabel = useWorkspaceSublabel();

  const isLoading = isAuthLoading;
  const locations = accountLocations;

  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [listPosition, setListPosition] = useState<FloatingListPosition | null>(null);
  const autoSelectAttemptRef = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const updateListPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setListPosition(computeFloatingPosition(rect, collapsed ? 220 : rect.width));
  }, [collapsed]);

  useLayoutEffect(() => {
    if (!open) {
      setListPosition(null);
      return;
    }
    updateListPosition();
  }, [open, updateListPosition, locations.length]);

  useEffect(() => {
    if (!open) return;

    const onReposition = () => {
      updateListPosition();
    };

    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateListPosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  useEffect(() => {
    if (isLoading || locations.length === 0 || switching) return;

    const sessionKey = locations.map((location) => location.id).join('|');
    if (autoSelectAttemptRef.current !== sessionKey) {
      autoSelectAttemptRef.current = null;
    }

    const exists =
      selectedLocationUuid != null && locations.some((l) => l.id === selectedLocationUuid);
    const first = locations[0];
    if (!exists && first && autoSelectAttemptRef.current !== sessionKey) {
      autoSelectAttemptRef.current = sessionKey;
      void switchActiveLocation(first.id).catch((error) => {
        console.error('Failed to auto-select location:', error);
      });
    }
  }, [isLoading, locations, selectedLocationUuid, switching, switchActiveLocation]);

  const selected =
    locations.find((l) => l.id === selectedLocationUuid) ?? locations[0];

  const locationTitle = isLoading
    ? 'Loading locations'
    : (selected?.name ?? (locations.length === 0 ? 'No locations' : 'Select location'));

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleSelect = async (location: LocationSummary) => {
    if (location.id === selectedLocationUuid || switching) {
      setOpen(false);
      return;
    }

    setSwitching(true);
    try {
      await switchActiveLocation(location.id);
      setSelectedLocationUuid(location.id);
      const coreId = resolveCoreLocationNumericId(location, coreLocations, {
        accountLocationCount: locations.length,
        ...(location.id === activeAccessLocation && accessLocationAscendId !== undefined
          ? { accessLocationAscendId }
          : {}),
      });
      setSelectedCoreLocationId(coreId ?? null);
    } catch (error) {
      console.error('Failed to switch location:', error);
    } finally {
      setSwitching(false);
      setOpen(false);
    }
  };

  const floatingList =
    open && locations.length > 0 && listPosition
      ? createPortal(
          <LocationList
            ref={listRef}
            locations={locations}
            {...(selected?.id !== undefined ? { selectedId: selected.id } : {})}
            position={listPosition}
            onSelect={(location) => {
              void handleSelect(location);
            }}
          />,
          document.body,
        )
      : null;

  if (collapsed) {
    return (
      <div className="relative flex justify-center" ref={rootRef}>
        <button
          ref={triggerRef}
          type="button"
          title={locationTitle}
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={isLoading || locations.length === 0 || switching}
          onClick={toggleOpen}
          className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-[7px] bg-gradient-to-br from-[var(--gold)] to-[#8a5e22] text-[11px] font-bold text-white">
            {initialsFromName(locationTitle).slice(0, 2)}
          </span>
        </button>
        {floatingList}
      </div>
    );
  }

  const isHeader = placement === 'header';

  return (
    <div className={cn('relative shrink-0', isHeader && 'min-w-[180px] max-w-[240px]')} ref={rootRef}>
      {isHeader ? null : <div className={cn(eyebrowClass, 'px-2 pb-2')}>Workspace</div>}
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={isLoading || locations.length === 0 || switching}
        onClick={toggleOpen}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-[13px] border border-[var(--ws-card-border)] bg-[var(--ws-card-bg)] px-[11px] py-[9px] text-left font-[inherit] shadow-[var(--ws-card-shadow)] [backdrop-filter:var(--ws-card-backdrop)] [-webkit-backdrop-filter:var(--ws-card-backdrop)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
          isHeader ? 'w-full px-3 py-2' : 'w-full px-[11px] py-[9px]',
        )}
      >
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] bg-gradient-to-br from-[var(--gold)] to-[#8a5e22] text-[11px] font-bold text-white">
          {initialsFromName(locationTitle).slice(0, 2)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-[var(--sidebar-ink-strong)]">
            {locationTitle}
          </span>
          {isHeader ? null : (
            <span className="mt-px block truncate text-[11px] text-[var(--sidebar-muted)]">
              {workspaceSublabel ?? (isLoading || switching ? 'Loading…' : '—')}
            </span>
          )}
        </span>
        <Icon kind="down" size={14} color="var(--sidebar-icon)" />
      </button>
      {floatingList}
    </div>
  );
}

const LocationList = React.forwardRef<
  HTMLUListElement,
  {
    locations: LocationSummary[];
    selectedId?: string;
    position: FloatingListPosition;
    onSelect: (location: LocationSummary) => void;
  }
>(function LocationList({ locations, selectedId, position, onSelect }, ref) {
  return (
    <ul
      ref={ref}
      role="listbox"
      style={{
        position: 'fixed',
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
        ...(position.top !== undefined ? { top: position.top } : {}),
        ...(position.bottom !== undefined ? { bottom: position.bottom } : {}),
        zIndex: 'var(--z-dropdown)',
      }}
      className="z-[var(--z-dropdown)] overflow-y-auto rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-0)] py-1 shadow-[var(--sh-3)]"
    >
      {locations.map((loc) => (
        <li key={loc.id} role="option" aria-selected={loc.id === selectedId}>
          <button
            type="button"
            className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-[var(--cream-1)]"
            onClick={() => {
              onSelect(loc);
            }}
          >
            <span className="truncate font-semibold text-[var(--ink-0)]">{loc.name}</span>
            {loc.ascend_id ? (
              <span className="truncate text-xs text-[var(--ink-3)]">{loc.ascend_id}</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
});
